import { useMemo } from "react";
import { last } from "@common/helpers/collections";
import { Transaction } from "@common/types/transaction";
import {
    AuditLog,
    AuditLogChangeType,
    AuditLogDiff,
    ResourceType,
} from "@common/types/auditLog";
import { useUncategorizedCategory } from "@hooks/useUncategorizedCategory";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { excludeCategoryDiffs } from "@components/history/details/utils/historyDetailsHelpers";
import { SCHEDULE_RULE_SKIP_AUDIT_FIELD } from "@common/schemas/domains/accounting/scheduleRuleOutcome.schema";

const FILTERED_FIELDS = [
    "categoryId",
    "splitParentId",
    "entityId",
    "counterpartyId",
    "accountId",
    "accrualAccountId",
    "generalLedgerAccountId",
    "pairedTransferId",
    "subaccountId",
    "memo",
    "accrualMatches",
    SCHEDULE_RULE_SKIP_AUDIT_FIELD,
];

export function useTransactionDisplayedLogs(
    transaction: (Transaction & { auditLogs?: AuditLog[] }) | undefined,
): AuditLog[] {
    const uncategorized = useUncategorizedCategory();
    const { enabled: isGlFirst } = useIsGlFirst();
    return useMemo(() => {
        const filteredLogs =
            transaction?.auditLogs?.reduce((acc: AuditLog[], log: AuditLog) => {
                const isFieldReview =
                    log.resourceType === ResourceType.TRANSACTION_FIELD_REVIEW;
                const isCreate = log.changeType === AuditLogChangeType.CREATE;

                // GL-first workspaces do not classify by category, so the
                // category portion of a log is hidden. Strip the `categoryId`
                // diff and re-evaluate: category-only logs become
                // non-renderable and are dropped, while logs that also carry
                // another change (e.g. a GL account update) keep that change.
                const displayDiff = isGlFirst
                    ? excludeCategoryDiffs(log.diff)
                    : log.diff;

                const hasDisplayableField = displayDiff.some(
                    (diff) =>
                        diff.field && FILTERED_FIELDS.includes(diff.field),
                );

                if (isFieldReview || isCreate || hasDisplayableField) {
                    acc.push(isGlFirst ? { ...log, diff: displayDiff } : log);
                }
                return acc;
            }, [] as AuditLog[]) ?? [];
        const lastLog = last(filteredLogs);

        if (
            lastLog?.changeType === AuditLogChangeType.UPDATE &&
            (lastLog.diff as AuditLogDiff[]).find(
                (diff) => diff.field === "categoryId",
            )?.after?.value === uncategorized?.id &&
            !(lastLog.diff as AuditLogDiff[]).find(
                (diff) => diff.field === "generalLedgerAccountId",
            )
        ) {
            filteredLogs?.pop();
        }

        return filteredLogs;
    }, [transaction?.auditLogs, uncategorized?.id, isGlFirst]);
}
